import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Mail, Sparkles, Loader2 } from "lucide-react";
import ComplianceStrip from "../components/ComplianceStrip";
import Seo from "../components/Seo";
import SeoProse from "../components/SeoProse";
import SurveyQuoteCTA from "../components/SurveyQuoteCTA";
import ConveyancingCTA from "../components/ConveyancingCTA";
import BrokerCTA from "../components/BrokerCTA";
import ShareActions from "../components/ShareActions";
import { CashbackPromptCard } from "../components/CashbackPrompt";
import { cashbackPromotionEnabled } from "../config/promotions";
import { toast } from "sonner";
import { api, track } from "../lib/api";
import { fmtGBP } from "../lib/calc";
import { PAGES } from "../content/site";

const journeys = [
  { id: "first_time_buyer", label: "First-time buyer", desc: "Buying your first home in the UK." },
  { id: "moving_home", label: "Moving home", desc: "Selling and buying another property." },
  { id: "remortgage", label: "Remortgaging", desc: "Switching deals on a property you own." },
  { id: "buy_to_let_other", label: "Buy-to-let / other", desc: "Investment or specialist lending." },
];

const STEPS = ["Journey", "Property", "Income", "Details", "Snapshot"];

const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Mx"];

const getStepBubbleClass = (done, active) => {
  if (done) return "bg-[#0F766E] text-white";
  if (active) return "bg-[#0B5F58] text-white";
  return "bg-white border border-[#DCE7E3] text-[#475569]";
};

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs">
    {STEPS.map((s, i) => {
      const active = i + 1 === step;
      const done = i + 1 < step;
      return (
        <li key={s} className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-full grid place-items-center font-semibold transition-colors ${getStepBubbleClass(done, active)}`}>
            {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
          </span>
          <span className={`hidden sm:inline ${active ? "text-[#0B1220] font-medium" : "text-[#475569]"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className="hidden sm:block w-6 h-px bg-[#DCE7E3]" />}
        </li>
      );
    })}
  </ol>
);

const parseFieldValue = (rawValue, type) => {
  if (type !== "number") return rawValue;
  if (rawValue === "") return "";
  return +rawValue;
};

const Field = ({ label, value, onChange, type = "number", step, testid, hint, placeholder, error, required }) => (
  <label className="block">
    <span className="mp-label">{label}{required && <span className="text-[#0F766E]"> *</span>}</span>
    <input
      type={type}
      step={step}
      placeholder={placeholder}
      className="mp-input"
      value={value ?? ""}
      onChange={(e) => onChange(parseFieldValue(e.target.value, type))}
      data-testid={testid}
    />
    {hint && !error && <span className="mp-help">{hint}</span>}
    {error && <span className="block mt-1 text-xs text-[#B91C1C]">{error}</span>}
  </label>
);

const initialForm = {
  journey: "first_time_buyer",
  title: "",
  first_name: "",
  last_name: "",
  property_value: "",
  deposit: "",
  current_mortgage_balance: "",
  term_years: 25,
  interest_rate: 4.75,
  income_1: "",
  income_2: "",
  monthly_commitments: "",
  dependants: "",
  notes: "",
  email: "",
  consent: false,
};

const validateStep = (s, form) => {
  const isRemortgage = form.journey === "remortgage";
  const e = {};
  if (s === 2) {
    if (!(+form.property_value > 0)) e.property_value = "Please enter the property price.";
    if (form.deposit === "" || +form.deposit < 0) e.deposit = isRemortgage ? "Please enter your equity." : "Please enter your deposit.";
    if (isRemortgage && !(+form.current_mortgage_balance > 0)) e.current_mortgage_balance = "Please enter your current balance.";
    if (!(+form.term_years >= 5 && +form.term_years <= 40)) e.term_years = "Enter a term of 5–40 years.";
    if (!(+form.interest_rate >= 0.1 && +form.interest_rate <= 20)) e.interest_rate = "Enter a rate of 0.1–20%.";
  }
  if (s === 3) {
    if (!(+form.income_1 > 0)) e.income_1 = "Please enter your annual income.";
  }
  if (s === 4) {
    if (!String(form.first_name).trim()) e.first_name = "Please enter your first name.";
    if (!String(form.last_name).trim()) e.last_name = "Please enter your last name.";
    if (!String(form.email).trim()) e.email = "Please enter your email address.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.consent) e.consent = "Please tick the consent box to continue.";
  }
  return e;
};

const StepJourney = ({ journey, onSelect }) => (
  <div className="mt-8">
    <h2 className="font-sora text-2xl font-semibold text-[#0B1220]">What best describes you?</h2>
    <div className="grid sm:grid-cols-2 gap-4 mt-5">
      {journeys.map((j) => {
        const active = journey === j.id;
        return (
          <button
            key={j.id}
            onClick={() => onSelect(j.id)}
            data-testid={`journey-${j.id}`}
            className={`text-left p-5 rounded-2xl border transition-all ${active ? "border-[#0F766E] bg-[#0F766E]/5 ring-2 ring-[#0F766E]/20" : "border-[#DCE7E3] bg-white hover:border-[#0F766E]/60"}`}
          >
            <div className="font-sora font-semibold text-[#0B1220]">{j.label}</div>
            <div className="text-sm text-[#475569] mt-1">{j.desc}</div>
          </button>
        );
      })}
    </div>
  </div>
);

const StepProperty = ({ form, set, errors, isRemortgage }) => (
  <div className="mt-8">
    <h2 className="font-sora text-2xl font-semibold text-[#0B1220]">Property and {isRemortgage ? "equity" : "deposit"}</h2>
    <div className="grid sm:grid-cols-2 gap-5 mt-5">
      <Field label="Estimated property price (£)" value={form.property_value} onChange={(v) => set({ property_value: v })} testid="snap-property-value" placeholder="e.g. 300,000" required error={errors.property_value} />
      <Field label={isRemortgage ? "Current equity (£)" : "Deposit (£)"} value={form.deposit} onChange={(v) => set({ deposit: v })} testid="snap-deposit" placeholder="e.g. 30,000" required error={errors.deposit} />
      {isRemortgage && (
        <Field label="Current mortgage balance (£)" value={form.current_mortgage_balance} onChange={(v) => set({ current_mortgage_balance: v })} testid="snap-current-balance" placeholder="e.g. 180,000" required error={errors.current_mortgage_balance} />
      )}
      <Field label="Desired mortgage term (years)" value={form.term_years} onChange={(v) => set({ term_years: v })} testid="snap-term" hint="Most UK mortgages run 25–35 years." required error={errors.term_years} />
      <Field label="Estimated interest rate (%)" value={form.interest_rate} onChange={(v) => set({ interest_rate: v })} step="0.05" testid="snap-rate" hint="A sensible default — edit if you have a known quote." required error={errors.interest_rate} />
    </div>
  </div>
);

const StepIncome = ({ form, set, errors, totalIncome }) => (
  <div className="mt-8">
    <h2 className="font-sora text-2xl font-semibold text-[#0B1220]">Income and commitments</h2>
    <div className="grid sm:grid-cols-2 gap-5 mt-5">
      <Field label="Applicant 1 — annual income (£)" value={form.income_1} onChange={(v) => set({ income_1: v })} testid="snap-income-1" placeholder="e.g. 45,000" required error={errors.income_1} />
      <Field label="Applicant 2 — annual income (£, optional)" value={form.income_2} onChange={(v) => set({ income_2: v })} testid="snap-income-2" placeholder="0" />
      <Field label="Monthly credit commitments (£)" value={form.monthly_commitments} onChange={(v) => set({ monthly_commitments: v })} testid="snap-commitments" hint="Loans, credit cards, car finance. Leave blank if none." placeholder="0" />
      <Field label="Dependants" value={form.dependants} onChange={(v) => set({ dependants: v })} testid="snap-dependants" placeholder="0" hint="Leave blank if none." />
      <label className="block sm:col-span-2">
        <span className="mp-label">Any major known issues? (optional)</span>
        <textarea className="mp-input" rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="e.g. recent IVA, planned career break" data-testid="snap-notes" />
      </label>
    </div>
    <p className="mt-4 text-xs text-[#475569]">Combined annual income entered: <strong>{fmtGBP(totalIncome)}</strong></p>
  </div>
);

const StepDetails = ({ form, set, errors }) => (
  <div className="mt-8">
    <h2 className="font-sora text-2xl font-semibold text-[#0B1220] flex items-center gap-2"><Mail className="w-5 h-5 text-[#0F766E]" /> Your details</h2>
    <p className="text-sm text-[#475569] mt-2">Tell us who you are and where to send your snapshot. We'll email you a copy with the key figures and next steps.</p>
    <div className="grid sm:grid-cols-2 gap-5 mt-5">
      <label className="block">
        <span className="mp-label">Title</span>
        <select className="mp-input" value={form.title} onChange={(e) => set({ title: e.target.value })} data-testid="snap-title">
          <option value="">Optional</option>
          {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <div className="hidden sm:block" aria-hidden="true" />
      <Field label="First name" type="text" value={form.first_name} onChange={(v) => set({ first_name: v })} testid="snap-first-name" placeholder="e.g. Alex" required error={errors.first_name} />
      <Field label="Last name" type="text" value={form.last_name} onChange={(v) => set({ last_name: v })} testid="snap-last-name" placeholder="e.g. Taylor" required error={errors.last_name} />
      <div className="sm:col-span-2">
        <Field label="Email address" type="email" value={form.email} onChange={(v) => set({ email: v })} testid="snap-email" placeholder="you@example.com" required error={errors.email} />
      </div>
    </div>
    <div className="mt-5 space-y-3">
      <label className="flex gap-3 items-start text-sm text-[#0B1220]">
        <input type="checkbox" checked={form.consent} onChange={(e) => set({ consent: e.target.checked })} data-testid="snap-consent" className="mt-1 w-4 h-4 accent-[#0F766E]" />
        <span>I understand this is educational information only and not mortgage advice. I agree to my details being processed to generate and email my MortgagePath Snapshot, in line with the <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#0F766E] underline">Privacy Policy</a>.</span>
      </label>
      {errors.consent && <span className="block text-xs text-[#B91C1C]" data-testid="snap-consent-error">{errors.consent}</span>}
    </div>
    <p className="mt-5 text-xs text-[#475569] leading-relaxed">
      MortgagePath does not provide regulated mortgage advice or recommend mortgage products or lenders.
    </p>
  </div>
);

const WizardControls = ({ step, back, next, submit, submitting }) => (
  <div className="mt-10 flex items-center justify-between">
    <button onClick={back} disabled={step === 1} className="mp-btn-secondary disabled:opacity-50" data-testid="snap-back">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
    {step < 4 ? (
      <button onClick={next} className="mp-btn-primary" data-testid="snap-next">
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    ) : (
      <button onClick={submit} disabled={submitting} className="mp-btn-primary disabled:opacity-60" data-testid="snap-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <>View my snapshot <ArrowRight className="w-4 h-4" /></>}
      </button>
    )}
  </div>
);

const Snapshot = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [params] = useSearchParams();
  const source = params.get("source") || "direct";

  // Mount-only view event; `track` is a stable module import (not a reactive dep).
  useEffect(() => { track("snapshot_view"); }, []);

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const keys = Object.keys(patch);
      if (!keys.some((k) => e[k])) return e;
      const n = { ...e };
      keys.forEach((k) => delete n[k]);
      return n;
    });
  };
  const isRemortgage = form.journey === "remortgage";

  const next = () => {
    const e = validateStep(step, form);
    if (Object.keys(e).length) { setErrors(e); toast.error("Please complete the required fields."); return; }
    if (step === 1) { track("mortgage_snapshot_started", { buyer_type: form.journey, source }); }
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  // form.income_1 and form.income_2 are the only fields read — listing
  // the whole `form` would needlessly retrigger this memo on every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalIncome = useMemo(() => (+form.income_1 || 0) + (+form.income_2 || 0), [form.income_1, form.income_2]);

  const submit = async () => {
    const e = validateStep(4, form);
    if (Object.keys(e).length) { setErrors(e); toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    track("snapshot_email_captured");
    try {
      const payload = { ...form,
        property_value: +form.property_value || 0,
        deposit: +form.deposit || 0,
        current_mortgage_balance: +form.current_mortgage_balance || 0,
        term_years: +form.term_years || 25,
        interest_rate: +form.interest_rate || 4.75,
        income_1: +form.income_1 || 0,
        income_2: +form.income_2 || 0,
        monthly_commitments: +form.monthly_commitments || 0,
        dependants: +form.dependants || 0,
      };
      const { data } = await api.post("/snapshot", payload);
      setResult(data);
      setStep(5);
      track("mortgage_snapshot_completed", { buyer_type: form.journey, source });
      if (data.email_sent) toast.success("Your snapshot has been emailed to you.");
      else toast.success("Your snapshot is ready below.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mp-container py-14 max-w-4xl">
      <Seo
        title="Free Mortgage Snapshot — your key UK mortgage figures"
        description="Get a free, educational Mortgage Snapshot in under 2 minutes. See your estimated loan, monthly repayment and loan-to-value with plain-English commentary. No credit check, no obligation."
        path="/snapshot"
      />
      <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Free Mortgage Snapshot</div>
      <h1 className="font-sora mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B1220]">Free UK Mortgage Snapshot</h1>
      <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">A clear explainer of the key figures lenders typically look at. No credit checks. No obligation.</p>

      <div className="mt-8 mp-card p-6 sm:p-8">
        <Stepper step={step} />
        {step === 1 && <StepJourney journey={form.journey} onSelect={(id) => set({ journey: id })} />}
        {step === 2 && <StepProperty form={form} set={set} errors={errors} isRemortgage={isRemortgage} />}
        {step === 3 && <StepIncome form={form} set={set} errors={errors} totalIncome={totalIncome} />}
        {step === 4 && <StepDetails form={form} set={set} errors={errors} />}
        {step === 5 && result && (
          <ResultsView result={result.results} form={form} emailSent={result.email_sent} />
        )}
        {step < 5 && <WizardControls step={step} back={back} next={next} submit={submit} submitting={submitting} />}
      </div>

      {step === 5 && result && (
        <>
          <BrokerCTA source="snapshot_results_broker" className="mt-8" />
          <div
            className="mt-5 grid gap-5 md:grid-cols-2"
            onClickCapture={() => track("next_step_clicked", { cta_location: "post_snapshot_next_step" })}
            data-testid="snap-post-next-steps"
          >
            <SurveyQuoteCTA source="snapshot_results_survey" variant="subtle" />
            <ConveyancingCTA source="snapshot_results_conveyancing" variant="subtle" />
          </div>
          {cashbackPromotionEnabled && (form.journey === "first_time_buyer" || form.journey === "moving_home") && (
            <div className="mt-5">
              <CashbackPromptCard source="snapshot" />
            </div>
          )}
        </>
      )}

      <SeoProse sections={PAGES["/snapshot"].sections} className="mt-12" />

      <div className="mt-8"><ComplianceStrip /></div>
    </div>
  );
};

/* Plain-text shareable summary of the CURRENT snapshot result. Uses the same
   figures rendered on-screen. No PII (no name/email), no partner names. */
const buildSnapshotShareText = (result, journey) => {
  const journeyLabel = journeys.find((j) => j.id === journey)?.label || "";
  return [
    `MortgagePath Mortgage Snapshot`,
    ``,
    ...(journeyLabel ? [`Journey: ${journeyLabel}`, ``] : []),
    `Your key figures:`,
    `• Estimated mortgage amount: ${fmtGBP(result.loan_amount)}`,
    `• Estimated monthly repayment: ${fmtGBP(result.monthly_repayment)}`,
    `• Loan-to-value: ${result.ltv_pct.toFixed(1)}%`,
    `• Typical borrowing band: ${fmtGBP(result.typical_borrow_low)} – ${fmtGBP(result.typical_borrow_high)}`,
    ``,
    `This is educational only — not a mortgage offer, recommendation or regulated advice. Speak to a qualified mortgage adviser before making a decision.`,
    ``,
    `Create your own free Snapshot at MortgagePath.uk/snapshot`,
  ].join("\n");
};

const ResultsView = ({ result, form, emailSent }) => {
  const shareText = buildSnapshotShareText(result, form.journey);
  return (
  <div className="mt-2" data-testid="snap-results">
    <div className="rounded-2xl bg-gradient-to-br from-[#0B5F58] to-[#10B981] text-white p-6 sm:p-8 relative overflow-hidden hero-noise">
      <div className="text-xs uppercase tracking-widest text-white/80">Your Mortgage Snapshot</div>
      <h2 className="font-sora mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Here are the key figures</h2>
      <p className="mt-2 text-white/85 text-sm max-w-2xl leading-relaxed">Based on what you entered, here are the key figures lenders would typically look at. This is educational only.</p>
      <p className="mt-3 text-xs text-white/75 leading-relaxed max-w-2xl">Educational only — figures are estimates, not a mortgage offer, recommendation, or regulated advice.</p>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="bg-white/10 border border-white/20 rounded-xl p-4">
          <div className="text-xs text-white/80">Estimated mortgage amount</div>
          <div className="font-sora text-2xl font-bold mt-1" data-testid="result-loan">{fmtGBP(result.loan_amount)}</div>
        </div>
        <div className="bg-white/10 border border-white/20 rounded-xl p-4">
          <div className="text-xs text-white/80">Estimated monthly repayment</div>
          <div className="font-sora text-2xl font-bold mt-1" data-testid="result-monthly">{fmtGBP(result.monthly_repayment)}</div>
        </div>
        <div className="bg-white/10 border border-white/20 rounded-xl p-4">
          <div className="text-xs text-white/80">Loan-to-value</div>
          <div className="font-sora text-2xl font-bold mt-1" data-testid="result-ltv">{result.ltv_pct.toFixed(1)}%</div>
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-3 gap-5 mt-6">
      <div className="mp-card p-6">
        <div className="text-xs uppercase tracking-widest text-[#0F766E]">Affordability</div>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed">{result.affordability_comment}</p>
        <p className="mt-3 text-xs text-[#0B1220]">Typical band ≈ {fmtGBP(result.typical_borrow_low)} – {fmtGBP(result.typical_borrow_high)}</p>
      </div>
      <div className="mp-card p-6">
        <div className="text-xs uppercase tracking-widest text-[#0F766E]">{form.journey === "remortgage" ? "Equity" : "Deposit"}</div>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed">{result.deposit_comment}</p>
      </div>
      <div className="mp-card p-6">
        <div className="text-xs uppercase tracking-widest text-[#0F766E]">LTV context</div>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed">{result.ltv_comment}</p>
      </div>
    </div>

    <div className="mp-card p-6 mt-6">
      <div className="text-xs uppercase tracking-widest text-[#0F766E]">Suggested next steps</div>
      <ul className="mt-3 space-y-2">
        {result.next_steps.map((s) => (
          <li key={s} className="text-sm text-[#0B1220] flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-1 shrink-0" /> {s}</li>
        ))}
      </ul>
    </div>

    <div className="rounded-2xl border border-[#DCE7E3] bg-[#F7FAF9] p-5 mt-6 text-sm text-[#0B1220] flex items-start gap-3">
      <Mail className="w-4 h-4 text-[#0F766E] mt-0.5" />
      <div>
        {emailSent
          ? <>A copy of your snapshot has been sent to <strong>{form.email}</strong>. Please check your inbox (and spam folder).</>
          : <>Your snapshot has been generated. We tried to email a copy to <strong>{form.email}</strong> — if it doesn't arrive, please check the address and try again.</>}
      </div>
    </div>

    <p className="mt-6 text-xs text-[#475569] leading-relaxed">
      Reminder: this snapshot is educational guidance only. It is not a mortgage offer, a regulated recommendation, or an indication that any lender will lend. Always speak to a qualified adviser before making a decision.
    </p>

    <ShareActions
      shareText={shareText}
      shareTitle="MortgagePath Mortgage Snapshot"
      copyEvent="snapshot_copy_result"
      shareEvent="snapshot_share_result"
      testIdPrefix="snap"
      copyLabel="Copy figures"
    />
  </div>
  );
};

export default Snapshot;
